import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, Plus, Edit, Trash2, Wrench, Clock, DollarSign, Library, Check, AlertTriangle, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { DealerService, CreateDealerServiceRequest, Dealer, ServiceType } from '../types';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import toast from 'react-hot-toast';

// Service Library - Top 25 most common auto services in the US
const SERVICE_LIBRARY = [
  {
    name: "Oil Change",
    description: "Standard engine oil and filter replacement service"
  },
  {
    name: "Tire Installation",
    description: "Mount and balance new tires on existing wheels"
  },
  {
    name: "Brake Inspection",
    description: "Comprehensive brake system inspection and assessment"
  },
  {
    name: "Wheel Alignment",
    description: "Adjust wheel angles to manufacturer specifications"
  },
  {
    name: "Battery Test & Replacement",
    description: "Test battery performance and replace if necessary"
  },
  {
    name: "Air Filter Replacement",
    description: "Replace engine air filter for optimal performance"
  },
  {
    name: "Brake Pad Replacement",
    description: "Replace worn brake pads with new components"
  },
  {
    name: "Tire Rotation",
    description: "Rotate tires to ensure even wear patterns"
  },
  {
    name: "Transmission Service",
    description: "Transmission fluid change and filter replacement"
  },
  {
    name: "Coolant System Flush",
    description: "Drain and refill cooling system with fresh coolant"
  },
  {
    name: "Spark Plug Replacement",
    description: "Replace spark plugs for improved engine performance"
  },
  {
    name: "Cabin Air Filter",
    description: "Replace cabin air filter for clean interior air"
  },
  {
    name: "Brake Rotor Replacement",
    description: "Replace worn or damaged brake rotors"
  },
  {
    name: "Wheel Balancing",
    description: "Balance wheels to eliminate vibration and uneven wear"
  },
  {
    name: "Engine Diagnostic",
    description: "Computer diagnostic scan to identify engine issues"
  },
  {
    name: "Fuel System Cleaning",
    description: "Clean fuel injectors and system components"
  },
  {
    name: "Serpentine Belt Replacement",
    description: "Replace worn serpentine belt and inspect pulleys"
  },
  {
    name: "Power Steering Service",
    description: "Power steering fluid change and system inspection"
  },
  {
    name: "Headlight Restoration",
    description: "Restore cloudy or yellowed headlight lenses"
  },
  {
    name: "Wiper Blade Replacement",
    description: "Replace worn windshield wiper blades"
  },
  {
    name: "Tire Pressure Check",
    description: "Check and adjust tire pressure to recommended levels"
  },
  {
    name: "Exhaust System Inspection",
    description: "Inspect exhaust system for leaks and damage"
  },
  {
    name: "Timing Belt Replacement",
    description: "Replace timing belt and inspect related components"
  },
  {
    name: "AC System Service",
    description: "Air conditioning system inspection and refrigerant service"
  },
  {
    name: "Multi-Point Inspection",
    description: "Comprehensive vehicle safety and maintenance inspection"
  }
];

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  
  h1 {
    margin: 0;
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
    display: flex;
    align-items: center;
    gap: 12px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 6px;
  text-decoration: none;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const DealerInfo = styled.div`
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  
  .dealer-name {
    font-weight: 600;
    color: #0c4a6e;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .dealer-details {
    font-size: 0.875rem;
    color: #0369a1;
  }
`;

const ServicesHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  
  h2 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
    color: #1f2937;
  }
  
  .header-buttons {
    display: flex;
    gap: 12px;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #ed2024 0%, #a01519 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 24px;
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  transition: all 0.2s;
  
  &:hover {
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.1);
  }
  
  .service-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 12px;
    
    .service-info {
      flex: 1;
      
      .service-name {
        font-weight: 600;
        color: #1f2937;
        font-size: 1.125rem;
        margin-bottom: 4px;
      }
      
      .service-description {
        font-size: 0.875rem;
        color: #6b7280;
        line-height: 1.4;
      }
    }
    
    .service-actions {
      display: flex;
      gap: 8px;
      margin-left: 12px;
    }
  }
  
  .service-meta {
    display: flex;
    gap: 16px;
    margin-top: 16px;
    
    .meta-item {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 0.875rem;
      color: #6b7280;
      
      svg {
        color: #9ca3af;
      }
      
      .value {
        font-weight: 600;
        color: #374151;
      }
    }
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: ${(props: { variant?: 'primary' | 'secondary' | 'danger' }) => 
    props.variant === 'primary' ? 'none' : 
    props.variant === 'danger' ? '1px solid #ef4444' : '1px solid #d1d5db'
  };
  background: ${(props: { variant?: 'primary' | 'secondary' | 'danger' }) => 
    props.variant === 'primary' ? 'linear-gradient(135deg, #ed2024 0%, #a01519 100%)' : 
    props.variant === 'danger' ? '#fef2f2' : 'white'
  };
  color: ${(props: { variant?: 'primary' | 'secondary' | 'danger' }) => 
    props.variant === 'primary' ? 'white' : 
    props.variant === 'danger' ? '#dc2626' : '#374151'
  };
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
  
  &:hover:not(:disabled) {
    ${(props: { variant?: 'primary' | 'secondary' | 'danger' }) => 
      props.variant === 'primary' ? `
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
      ` : props.variant === 'danger' ? `
        background: #fee2e2;
        border-color: #fca5a5;
      ` : `
        background: #f3f4f6;
        border-color: #9ca3af;
      `
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const Modal = styled.div<{ isOpen: boolean }>`
  display: ${(props: { isOpen: boolean }) => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  align-items: center;
  justify-content: center;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  
  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    
    h3 {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 600;
      color: #1f2937;
    }
    
    button {
      background: none;
      border: none;
      font-size: 1.5rem;
      color: #6b7280;
      cursor: pointer;
      padding: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      &:hover {
        color: #374151;
      }
    }
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
`;

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 10px 12px;
  border: 2px solid ${(props: { hasError?: boolean }) => props.hasError ? '#ef4444' : '#e5e7eb'};
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: ${(props: { hasError?: boolean }) => props.hasError ? '#ef4444' : '#ed2024'};
    box-shadow: 0 0 0 3px ${(props: { hasError?: boolean }) => props.hasError ? 'rgba(239, 68, 68, 0.1)' : 'rgba(102, 126, 234, 0.1)'};
  }
`;

const TextArea = styled.textarea<{ hasError?: boolean }>`
  width: 100%;
  padding: 10px 12px;
  border: 2px solid ${(props: { hasError?: boolean }) => props.hasError ? '#ef4444' : '#e5e7eb'};
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.2s;
  resize: vertical;
  min-height: 80px;
  
  &:focus {
    outline: none;
    border-color: ${(props: { hasError?: boolean }) => props.hasError ? '#ef4444' : '#ed2024'};
    box-shadow: 0 0 0 3px ${(props: { hasError?: boolean }) => props.hasError ? 'rgba(239, 68, 68, 0.1)' : 'rgba(102, 126, 234, 0.1)'};
  }
`;

const ErrorMessage = styled.span`
  color: #ef4444;
  font-size: 0.875rem;
`;

const HelpText = styled.span`
  color: #6b7280;
  font-size: 0.75rem;
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top: 3px solid #ed2024;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
  
  h3 {
    margin: 0 0 8px;
    color: #374151;
  }
  
  p {
    margin: 0 0 20px;
  }
`;

const LibraryModal = styled(Modal)`
  .modal-content {
    max-width: 800px;
    width: 95%;
    max-height: 85vh;
  }
`;

const LibraryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 16px;
  max-height: 60vh;
  overflow-y: auto;
  padding: 4px;
  margin: 20px 0;
`;

const LibraryServiceCard = styled.div<{ isSelected: boolean; isDuplicate: boolean }>`
  background: ${(props: { isSelected: boolean; isDuplicate: boolean }) => props.isSelected ? '#f0f9ff' : 'white'};
  border: 2px solid ${(props: { isSelected: boolean; isDuplicate: boolean }) => 
    props.isDuplicate ? '#fbbf24' : 
    props.isSelected ? '#3b82f6' : '#e5e7eb'
  };
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  
  &:hover {
    border-color: ${(props: { isSelected: boolean; isDuplicate: boolean }) => 
      props.isDuplicate ? '#f59e0b' : 
      props.isSelected ? '#2563eb' : '#9ca3af'
    };
    background: ${(props: { isSelected: boolean; isDuplicate: boolean }) => props.isSelected ? '#eff6ff' : '#f9fafb'};
  }
  
  .service-header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 8px;
    
    .checkbox {
      width: 20px;
      height: 20px;
      border: 2px solid ${(props: { isSelected: boolean; isDuplicate: boolean }) => props.isSelected ? '#3b82f6' : '#d1d5db'};
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: ${(props: { isSelected: boolean; isDuplicate: boolean }) => props.isSelected ? '#3b82f6' : 'white'};
      flex-shrink: 0;
      margin-top: 2px;
      
      svg {
        color: white;
        width: 14px;
        height: 14px;
      }
    }
    
    .service-info {
      flex: 1;
      
      .service-name {
        font-weight: 600;
        color: #1f2937;
        font-size: 1rem;
        margin-bottom: 4px;
      }
      
      .service-description {
        font-size: 0.875rem;
        color: #6b7280;
        line-height: 1.4;
      }
    }
  }
  
  .service-meta {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
    font-size: 0.75rem;
    color: #9ca3af;
    
    .duration {
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }
  
  ${(props: { isSelected: boolean; isDuplicate: boolean }) => props.isDuplicate && `
    .duplicate-warning {
      position: absolute;
      top: 8px;
      right: 8px;
      background: #fbbf24;
      color: white;
      border-radius: 50%;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      svg {
        width: 12px;
        height: 12px;
      }
    }
  `}
`;

const LibraryActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
  
  .selection-info {
    font-size: 0.875rem;
    color: #6b7280;
    
    .count {
      font-weight: 600;
      color: #374151;
    }
  }
  
  .action-buttons {
    display: flex;
    gap: 12px;
  }
`;

const DuplicateWarning = styled.div`
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 6px;
  padding: 12px;
  margin: 16px 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  
  .warning-icon {
    color: #f59e0b;
    flex-shrink: 0;
    margin-top: 1px;
  }
  
  .warning-content {
    flex: 1;
    
    .warning-title {
      font-weight: 600;
      color: #92400e;
      margin-bottom: 4px;
    }
    
    .warning-text {
      font-size: 0.875rem;
      color: #a16207;
      line-height: 1.4;
    }
    
    .duplicate-list {
      margin-top: 8px;
      padding-left: 16px;
      
      li {
        font-size: 0.875rem;
        color: #a16207;
        margin-bottom: 2px;
      }
    }
  }
`;

const TwhdSection = styled.div`
  margin-bottom: 40px;
`;

const TwhdHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  h2 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
    color: #1f2937;
  }

  .twhd-actions {
    display: flex;
    gap: 12px;
    align-items: center;
  }
`;

const TwhdInfoBanner = styled.div`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  font-size: 0.875rem;
  color: #1e40af;
  line-height: 1.5;

  strong {
    font-weight: 600;
  }
`;

const TwhdSaveBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;

  .save-status {
    font-size: 0.875rem;
    color: #374151;

    .count {
      font-weight: 600;
      color: #1f2937;
    }
  }
`;

const TwhdServicesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TwhdServiceRow = styled.div<{ visible: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: ${(props: { visible: boolean }) => props.visible ? 'white' : '#f9fafb'};
  border: 1px solid ${(props: { visible: boolean }) => props.visible ? '#e5e7eb' : '#f3f4f6'};
  border-radius: 8px;
  transition: all 0.15s;

  &:hover {
    border-color: #d1d5db;
    background: ${(props: { visible: boolean }) => props.visible ? '#fafafa' : '#f3f4f6'};
  }

  .service-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;

    .service-name {
      font-weight: 600;
      font-size: 0.95rem;
      color: ${(props: { visible: boolean }) => props.visible ? '#1f2937' : '#9ca3af'};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .service-desc {
      font-size: 0.8rem;
      color: #9ca3af;
      margin-top: 2px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .service-meta {
      display: flex;
      gap: 12px;
      font-size: 0.75rem;
      color: #9ca3af;
      margin-top: 4px;

      span {
        display: flex;
        align-items: center;
        gap: 3px;
      }
    }
  }

  .service-right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
    margin-left: 12px;

    .badge {
      font-size: 0.7rem;
      font-weight: 600;
      padding: 2px 8px;
      border-radius: 100px;
      background: ${(props: { visible: boolean }) => props.visible ? '#dcfce7' : '#f3f4f6'};
      color: ${(props: { visible: boolean }) => props.visible ? '#16a34a' : '#9ca3af'};
    }
  }
`;

const ToggleButton = styled.button<{ visible: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid ${(props: { visible: boolean }) => props.visible ? '#ef4444' : '#22c55e'};
  background: transparent;
  color: ${(props: { visible: boolean }) => props.visible ? '#ef4444' : '#22c55e'};
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;

  &:hover {
    background: ${(props: { visible: boolean }) => props.visible ? '#fef2f2' : '#f0fdf4'};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const SectionDivider = styled.div`
  border-top: 1px solid #e5e7eb;
  margin: 32px 0;
`;

const DealerServicesPage: React.FC = () => {
  const { dealerId } = useParams<{ dealerId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dealer, setDealer] = useState<Dealer | null>(null);
  const [services, setServices] = useState<DealerService[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isLibraryModalOpen, setIsLibraryModalOpen] = useState(false);
  const [editingService, setEditingService] = useState<DealerService | null>(null);
  const [selectedLibraryServices, setSelectedLibraryServices] = useState<Set<string>>(new Set());
  const [isAddingFromLibrary, setIsAddingFromLibrary] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    durationMinutes: '',
    price: '',
    twhdPackageCode: ''
  });

  // TWHD service visibility state
  const [twhdServices, setTwhdServices] = useState<ServiceType[]>([]);
  const [twhdAllowed, setTwhdAllowed] = useState<Set<string>>(new Set());
  const [twhdAllowedDirty, setTwhdAllowedDirty] = useState(false);
  const [isLoadingTwhd, setIsLoadingTwhd] = useState(false);
  const [isSavingTwhd, setIsSavingTwhd] = useState(false);

  // Determine the actual dealer ID to use
  const actualDealerId = user?.userType === 'admin' ? dealerId : user?.userType === 'dealer' ? user.dealerId : null;

  useEffect(() => {
    if (actualDealerId) {
      loadDealerAndServices();
    }
  }, [actualDealerId]);

  const loadDealerAndServices = async () => {
    if (!actualDealerId) return;

    try {
      setIsLoading(true);
      
      // Load dealer info
      const dealerResponse = await apiService.getDealer(actualDealerId);
      if (dealerResponse.success && dealerResponse.data) {
        setDealer(dealerResponse.data);
        // Kick off TWHD catalog load if this dealer uses TireWorks integration
        if (dealerResponse.data.useThirdPartyIntegration && dealerResponse.data.thirdPartyProvider === 'tireworkshd') {
          loadTwhdServices(actualDealerId);
        }
      }

      // Load custom (manual) services
      const servicesResponse = await apiService.getDealerServices(actualDealerId);
      if (servicesResponse.success && servicesResponse.data) {
        setServices(servicesResponse.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load services');
    } finally {
      setIsLoading(false);
    }
  };

  const loadTwhdServices = async (dealerId: string) => {
    setIsLoadingTwhd(true);
    try {
      const response = await apiService.getDealerTwhdServices(dealerId);
      if (response.success && response.data) {
        setTwhdServices(response.data.services);
        // If allowedServices is empty it means "show all" — treat all as visible
        const allowed = response.data.allowedServices;
        if (allowed.length === 0) {
          setTwhdAllowed(new Set(response.data.services.map(s => s.id)));
        } else {
          setTwhdAllowed(new Set(allowed));
        }
        setTwhdAllowedDirty(false);
      }
    } catch (error) {
      // Non-fatal: TWHD may be temporarily unreachable
      console.error('Error loading TireWorks HD services:', error);
      toast.error('Could not reach TireWorks HD to load services. Check credentials and try again.');
    } finally {
      setIsLoadingTwhd(false);
    }
  };

  const toggleTwhdServiceVisibility = (serviceId: string) => {
    setTwhdAllowed(prev => {
      const next = new Set(prev);
      if (next.has(serviceId)) {
        next.delete(serviceId);
      } else {
        next.add(serviceId);
      }
      return next;
    });
    setTwhdAllowedDirty(true);
  };

  const saveTwhdVisibility = async () => {
    if (!actualDealerId) return;
    setIsSavingTwhd(true);
    try {
      // Convert set to array. If every service is allowed, send [] to mean "show all".
      const allIds = twhdServices.map(s => s.id);
      const allowedArr = Array.from(twhdAllowed);
      const effectiveArr = allowedArr.length === allIds.length ? [] : allowedArr;
      const response = await apiService.updateAllowedServices(actualDealerId, effectiveArr);
      if (response.success) {
        setTwhdAllowedDirty(false);
        toast.success('Service visibility saved');
      } else {
        toast.error('Failed to save visibility settings');
      }
    } catch (error) {
      console.error('Error saving TWHD visibility:', error);
      toast.error('Failed to save visibility settings');
    } finally {
      setIsSavingTwhd(false);
    }
  };

  const handleAddService = async () => {
    if (!formData.name.trim() || !formData.durationMinutes.trim() || !actualDealerId) return;

    try {
      const serviceData: CreateDealerServiceRequest = {
        dealerId: actualDealerId,
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        durationMinutes: parseInt(formData.durationMinutes),
        price: formData.price.trim() ? parseFloat(formData.price) : undefined,
        twhdPackageCode: formData.twhdPackageCode.trim() || undefined
      };

      const response = await apiService.createDealerService(actualDealerId, serviceData);
      if (response.success && response.data) {
        setServices(prev => [...prev, response.data!].sort((a, b) => a.name.localeCompare(b.name)));
        setFormData({ name: '', description: '', durationMinutes: '', price: '', twhdPackageCode: '' });
        setIsModalOpen(false);
        toast.success('Service added successfully');
      } else {
        toast.error('Failed to add service');
      }
    } catch (error) {
      console.error('Error adding service:', error);
      toast.error('Failed to add service');
    }
  };

  const handleEditService = (service: DealerService) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description || '',
      durationMinutes: service.durationMinutes.toString(),
      price: service.price?.toString() || '',
      twhdPackageCode: service.twhdPackageCode || ''
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateService = async () => {
    if (!formData.name.trim() || !formData.durationMinutes.trim() || !editingService) return;

    try {
      const serviceData = {
        id: editingService.id,
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        durationMinutes: parseInt(formData.durationMinutes),
        price: formData.price.trim() ? parseFloat(formData.price) : undefined,
        twhdPackageCode: formData.twhdPackageCode.trim() || undefined
      };

      const response = await apiService.updateDealerService(editingService.id, serviceData);
      if (response.success && response.data) {
        setServices(prev => prev.map(service => 
          service.id === editingService.id ? response.data! : service
        ).sort((a, b) => a.name.localeCompare(b.name)));
        setIsEditModalOpen(false);
        setEditingService(null);
        setFormData({ name: '', description: '', durationMinutes: '', price: '', twhdPackageCode: '' });
        toast.success('Service updated successfully');
      } else {
        toast.error('Failed to update service');
      }
    } catch (error) {
      console.error('Error updating service:', error);
      toast.error('Failed to update service');
    }
  };

  const handleDeleteService = async (service: DealerService) => {
    if (!window.confirm(`Are you sure you want to delete "${service.name}"? This will also remove this service from all bay assignments.`)) {
      return;
    }

    try {
      const response = await apiService.deleteDealerService(service.id);
      if (response.success) {
        setServices(prev => prev.filter(s => s.id !== service.id));
        toast.success('Service deleted successfully');
      } else {
        toast.error('Failed to delete service');
      }
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Failed to delete service');
    }
  };

  const updateFormField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const openAddModal = () => {
    setFormData({ name: '', description: '', durationMinutes: '', price: '', twhdPackageCode: '' });
    setIsModalOpen(true);
  };

  const openLibraryModal = () => {
    setSelectedLibraryServices(new Set());
    setIsLibraryModalOpen(true);
  };

  const toggleLibraryService = (serviceName: string) => {
    const newSelected = new Set(selectedLibraryServices);
    if (newSelected.has(serviceName)) {
      newSelected.delete(serviceName);
    } else {
      newSelected.add(serviceName);
    }
    setSelectedLibraryServices(newSelected);
  };

  const getExistingServiceNames = (): Set<string> => {
    return new Set(services.map(service => service.name.toLowerCase()));
  };

  const getDuplicateServices = (): string[] => {
    const existingNames = getExistingServiceNames();
    return Array.from(selectedLibraryServices).filter(serviceName => 
      existingNames.has(serviceName.toLowerCase())
    );
  };

  const handleAddFromLibrary = async () => {
    if (selectedLibraryServices.size === 0 || !actualDealerId) return;

    const duplicates = getDuplicateServices();
    
    // If there are duplicates, show confirmation
    if (duplicates.length > 0) {
      const confirmMessage = `The following services already exist in your account:\n\n${duplicates.join('\n')}\n\nDo you want to add them anyway? This will create duplicate services.`;
      
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }

    try {
      setIsAddingFromLibrary(true);
      const servicesToAdd = Array.from(selectedLibraryServices);
      const addedServices: DealerService[] = [];

      // Add services one by one
      for (const serviceName of servicesToAdd) {
        const libraryService = SERVICE_LIBRARY.find(s => s.name === serviceName);
        if (!libraryService) continue;

        const serviceData: CreateDealerServiceRequest = {
          dealerId: actualDealerId,
          name: libraryService.name,
          description: libraryService.description,
          durationMinutes: 60, // Default 60 minutes
          price: undefined // No default price
        };

        const response = await apiService.createDealerService(actualDealerId, serviceData);
        if (response.success && response.data) {
          addedServices.push(response.data);
        }
      }

      if (addedServices.length > 0) {
        setServices(prev => [...prev, ...addedServices].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success(`Added ${addedServices.length} service${addedServices.length > 1 ? 's' : ''} from library`);
      }

      setIsLibraryModalOpen(false);
      setSelectedLibraryServices(new Set());
    } catch (error) {
      console.error('Error adding services from library:', error);
      toast.error('Failed to add services from library');
    } finally {
      setIsAddingFromLibrary(false);
    }
  };

  const handleBack = () => {
    if (user?.userType === 'dealer') {
      navigate('/dealer-dashboard');
    } else if (actualDealerId) {
      navigate(`/dealers/${actualDealerId}/edit`);
    } else {
      navigate('/dealers');
    }
  };

  const formatPrice = (price?: number): string => {
    if (price === undefined) return 'Not set';
    return `$${price.toFixed(2)}`;
  };

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) return `${hours}h`;
    return `${hours}h ${remainingMinutes}m`;
  };

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingSpinner>
          <div className="spinner" />
        </LoadingSpinner>
      </PageContainer>
    );
  }

  if (!actualDealerId) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
          Access denied. Unable to determine dealer context.
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <BackButton onClick={handleBack}>
          <ArrowLeft />
          Back
        </BackButton>
        <h1>
          <Wrench />
          Service Management
        </h1>
      </PageHeader>

      {dealer && (
        <DealerInfo>
          <div className="dealer-name">
            <Wrench size={16} />
            {dealer.name} - Services
          </div>
          <div className="dealer-details">
            Manage the services offered by this dealership
          </div>
        </DealerInfo>
      )}

      {/* TireWorks HD Service Visibility Section */}
      {dealer?.useThirdPartyIntegration && dealer?.thirdPartyProvider === 'tireworkshd' && (
        <>
          <TwhdSection>
            <TwhdHeader>
              <h2>TireWorks HD Services</h2>
              <div className="twhd-actions">
                {twhdAllowedDirty && (
                  <AddButton
                    onClick={saveTwhdVisibility}
                    disabled={isSavingTwhd}
                    style={{ background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)' }}
                  >
                    {isSavingTwhd ? 'Saving…' : 'Save Visibility'}
                  </AddButton>
                )}
                <AddButton
                  onClick={() => actualDealerId && loadTwhdServices(actualDealerId)}
                  disabled={isLoadingTwhd}
                  style={{ background: 'white', color: '#374151', border: '1px solid #d1d5db' }}
                >
                  <RefreshCw size={14} style={{ marginRight: 4 }} />
                  {isLoadingTwhd ? 'Loading…' : 'Refresh'}
                </AddButton>
              </div>
            </TwhdHeader>

            <TwhdInfoBanner>
              These are the services pulled directly from <strong>TireWorks HD</strong>. Toggle each service to control whether it is shown to customers in your booking widget. Hidden services still exist in TWHD but will not appear as options during online scheduling.
            </TwhdInfoBanner>

            {twhdAllowedDirty && (
              <TwhdSaveBar>
                <div className="save-status">
                  <span className="count">{twhdAllowed.size}</span> of {twhdServices.length} services visible to customers
                </div>
                <AddButton
                  onClick={saveTwhdVisibility}
                  disabled={isSavingTwhd}
                  style={{ background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', padding: '8px 16px' }}
                >
                  {isSavingTwhd ? 'Saving…' : 'Save Changes'}
                </AddButton>
              </TwhdSaveBar>
            )}

            {isLoadingTwhd ? (
              <LoadingSpinner><div className="spinner" /></LoadingSpinner>
            ) : twhdServices.length === 0 ? (
              <EmptyState>
                <h3>No TireWorks HD services found</h3>
                <p>Make sure your TireWorks HD credentials are configured and the location ID is correct.</p>
              </EmptyState>
            ) : (
              <TwhdServicesList>
                {twhdServices.map(service => {
                  const visible = twhdAllowed.has(service.id);
                  return (
                    <TwhdServiceRow key={service.id} visible={visible}>
                      <div className="service-left">
                        <div>
                          <div className="service-name">{service.name}</div>
                          {service.description && <div className="service-desc">{service.description}</div>}
                          <div className="service-meta">
                            <span><Clock size={11} />{service.estimatedDuration} min</span>
                            <span style={{ opacity: 0.6, fontSize: '0.7rem' }}>ID: {service.id}</span>
                          </div>
                        </div>
                      </div>
                      <div className="service-right">
                        <span className="badge">{visible ? 'Visible' : 'Hidden'}</span>
                        <ToggleButton visible={visible} onClick={() => toggleTwhdServiceVisibility(service.id)}>
                          {visible ? <><EyeOff />Hide</> : <><Eye />Show</>}
                        </ToggleButton>
                      </div>
                    </TwhdServiceRow>
                  );
                })}
              </TwhdServicesList>
            )}
          </TwhdSection>
          <SectionDivider />
        </>
      )}

      <ServicesHeader>
        <h2>Custom Services ({services.length})</h2>
        <div className="header-buttons">
          <AddButton onClick={openLibraryModal}>
            <Library />
            Service Library
          </AddButton>
          <AddButton onClick={openAddModal}>
            <Plus />
            Add Service
          </AddButton>
        </div>
      </ServicesHeader>

      {services.length === 0 ? (
        <EmptyState>
          <h3>No custom services</h3>
          <p>
            {dealer?.useThirdPartyIntegration
              ? 'Custom services are separate from TireWorks HD services above. You can add them here if you need additional booking options.'
              : 'Create your first service to get started with appointment scheduling.'}
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <AddButton onClick={openLibraryModal}>
              <Library />
              Browse Service Library
            </AddButton>
            <AddButton onClick={openAddModal}>
              <Plus />
              Add Custom Service
            </AddButton>
          </div>
        </EmptyState>
      ) : (
        <ServicesGrid>
          {services.map((service) => (
            <ServiceCard key={service.id}>
              <div className="service-header">
                <div className="service-info">
                  <div className="service-name">{service.name}</div>
                  {service.description && (
                    <div className="service-description">{service.description}</div>
                  )}
                </div>
                <div className="service-actions">
                  <Button onClick={() => handleEditService(service)}>
                    <Edit />
                  </Button>
                  <Button 
                    variant="danger" 
                    onClick={() => handleDeleteService(service)}
                  >
                    <Trash2 />
                  </Button>
                </div>
              </div>
              
              <div className="service-meta">
                <div className="meta-item">
                  <Clock size={14} />
                  <span className="value">{formatDuration(service.durationMinutes)}</span>
                </div>
                <div className="meta-item">
                  <DollarSign size={14} />
                  <span className="value">{formatPrice(service.price)}</span>
                </div>
              </div>
            </ServiceCard>
          ))}
        </ServicesGrid>
      )}

      {/* Add Service Modal */}
      <Modal isOpen={isModalOpen}>
        <ModalContent>
          <div className="modal-header">
            <h3>Add New Service</h3>
            <button onClick={() => setIsModalOpen(false)}>×</button>
          </div>
          
          <FormGroup>
            <Label htmlFor="serviceName">Service Name *</Label>
            <Input
              id="serviceName"
              value={formData.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateFormField('name', e.target.value)}
              placeholder="e.g., Oil Change, Tire Installation"
              hasError={false}
            />
            <HelpText>Enter a descriptive name for this service</HelpText>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="serviceDescription">Description</Label>
            <TextArea
              id="serviceDescription"
              value={formData.description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => updateFormField('description', e.target.value)}
              placeholder="Optional description of what this service includes"
              hasError={false}
            />
          </FormGroup>

          <FormRow>
            <FormGroup>
              <Label htmlFor="serviceDuration">Duration (minutes) *</Label>
              <Input
                id="serviceDuration"
                type="number"
                min="1"
                value={formData.durationMinutes}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateFormField('durationMinutes', e.target.value)}
                placeholder="60"
                hasError={false}
              />
              <HelpText>Estimated time to complete this service</HelpText>
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="servicePrice">Price ($)</Label>
              <Input
                id="servicePrice"
                type="number"
                min="0"
                step="0.01"
                value={formData.price}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateFormField('price', e.target.value)}
                placeholder="99.99"
                hasError={false}
              />
              <HelpText>Optional price for this service</HelpText>
            </FormGroup>
          </FormRow>

          {dealer?.useThirdPartyIntegration && (
            <FormGroup>
              <Label htmlFor="serviceTwhdCode">TireWorks HD Package Code</Label>
              <Input
                id="serviceTwhdCode"
                value={formData.twhdPackageCode}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateFormField('twhdPackageCode', e.target.value)}
                placeholder="e.g. OIL-FULL-SYN"
                hasError={false}
              />
              <HelpText>Optional. If this custom service maps to a TWHD package, enter its PackageCode here so appointments are recorded correctly in TireWorks HD.</HelpText>
            </FormGroup>
          )}
          
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
            <Button onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleAddService}
              disabled={!formData.name.trim() || !formData.durationMinutes.trim()}
            >
              Add Service
            </Button>
          </div>
        </ModalContent>
      </Modal>

      {/* Edit Service Modal */}
      <Modal isOpen={isEditModalOpen}>
        <ModalContent>
          <div className="modal-header">
            <h3>Edit Service</h3>
            <button onClick={() => setIsEditModalOpen(false)}>×</button>
          </div>
          
          <FormGroup>
            <Label htmlFor="editServiceName">Service Name *</Label>
            <Input
              id="editServiceName"
              value={formData.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateFormField('name', e.target.value)}
              placeholder="e.g., Oil Change, Tire Installation"
              hasError={false}
            />
            <HelpText>Enter a descriptive name for this service</HelpText>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="editServiceDescription">Description</Label>
            <TextArea
              id="editServiceDescription"
              value={formData.description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => updateFormField('description', e.target.value)}
              placeholder="Optional description of what this service includes"
              hasError={false}
            />
          </FormGroup>

          <FormRow>
            <FormGroup>
              <Label htmlFor="editServiceDuration">Duration (minutes) *</Label>
              <Input
                id="editServiceDuration"
                type="number"
                min="1"
                value={formData.durationMinutes}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateFormField('durationMinutes', e.target.value)}
                placeholder="60"
                hasError={false}
              />
              <HelpText>Estimated time to complete this service</HelpText>
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="editServicePrice">Price ($)</Label>
              <Input
                id="editServicePrice"
                type="number"
                min="0"
                step="0.01"
                value={formData.price}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateFormField('price', e.target.value)}
                placeholder="99.99"
                hasError={false}
              />
              <HelpText>Optional price for this service</HelpText>
            </FormGroup>
          </FormRow>

          {dealer?.useThirdPartyIntegration && (
            <FormGroup>
              <Label htmlFor="editServiceTwhdCode">TireWorks HD Package Code</Label>
              <Input
                id="editServiceTwhdCode"
                value={formData.twhdPackageCode}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateFormField('twhdPackageCode', e.target.value)}
                placeholder="e.g. OIL-FULL-SYN"
                hasError={false}
              />
              <HelpText>Optional. Maps this custom service to a TWHD PackageCode at booking time.</HelpText>
            </FormGroup>
          )}
          
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
            <Button onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleUpdateService}
              disabled={!formData.name.trim() || !formData.durationMinutes.trim()}
            >
              Update Service
            </Button>
          </div>
        </ModalContent>
      </Modal>

      {/* Service Library Modal */}
      <LibraryModal isOpen={isLibraryModalOpen}>
        <ModalContent className="modal-content">
          <div className="modal-header">
            <h3>Service Library</h3>
            <button onClick={() => setIsLibraryModalOpen(false)}>×</button>
          </div>
          
          <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 16px' }}>
            Select from our library of common auto services. Each service will be added with a 60-minute duration and can be customized after adding.
          </p>

          {getDuplicateServices().length > 0 && (
            <DuplicateWarning>
              <AlertTriangle className="warning-icon" size={16} />
              <div className="warning-content">
                <div className="warning-title">Duplicate Services Detected</div>
                <div className="warning-text">
                  The following services already exist in your account. You can still add them to create duplicates if needed.
                </div>
                <ul className="duplicate-list">
                  {getDuplicateServices().map(serviceName => (
                    <li key={serviceName}>• {serviceName}</li>
                  ))}
                </ul>
              </div>
            </DuplicateWarning>
          )}
          
          <LibraryGrid>
            {SERVICE_LIBRARY.map((service) => {
              const isSelected = selectedLibraryServices.has(service.name);
              const isDuplicate = getExistingServiceNames().has(service.name.toLowerCase());
              
              return (
                <LibraryServiceCard
                  key={service.name}
                  isSelected={isSelected}
                  isDuplicate={isDuplicate}
                  onClick={() => toggleLibraryService(service.name)}
                >
                  {isDuplicate && (
                    <div className="duplicate-warning">
                      <AlertTriangle />
                    </div>
                  )}
                  
                  <div className="service-header">
                    <div className="checkbox">
                      {isSelected && <Check />}
                    </div>
                    <div className="service-info">
                      <div className="service-name">{service.name}</div>
                      <div className="service-description">{service.description}</div>
                    </div>
                  </div>
                  
                  <div className="service-meta">
                    <div className="duration">
                      <Clock size={12} />
                      60 min
                    </div>
                  </div>
                </LibraryServiceCard>
              );
            })}
          </LibraryGrid>
          
          <LibraryActions>
            <div className="selection-info">
              <span className="count">{selectedLibraryServices.size}</span> service{selectedLibraryServices.size !== 1 ? 's' : ''} selected
            </div>
            <div className="action-buttons">
              <Button onClick={() => setIsLibraryModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleAddFromLibrary}
                disabled={selectedLibraryServices.size === 0 || isAddingFromLibrary}
              >
                {isAddingFromLibrary ? 'Adding...' : `Add ${selectedLibraryServices.size} Service${selectedLibraryServices.size !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </LibraryActions>
        </ModalContent>
      </LibraryModal>
    </PageContainer>
  );
};

export default DealerServicesPage;
